import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'

import { getPercentageFull } from 'utils/metrics'
import { startSessionRecording } from 'utils/mixpanel'
import { getTrialState, getCurrentOrganization } from 'ducks/organizations'
import { getDefaultDatasource } from 'ducks/apps/datasources'
import { loadMetrics, getMetrics } from 'ducks/metrics'

import Icon from 'components/Shared/Icon'
import { useFunnelTracking } from 'hooks/useFunnelTracking'

import Tooltip from 'components/Shared/Tooltip'
import Button from '../Button'
import './TrialWarning.scss'
import { getDaysLeftText } from '../FreeTrial/utils'

const TrialBanner = ({ onClick: handleClick, appId, bottomBar = true }) => {
  const dispatch = useDispatch()
  const { trackClickedUpgradeNow } = useFunnelTracking()
  const { days, trialState } = useSelector(getTrialState)

  const metrics = useSelector(state => getMetrics(state, appId))
  const datasource = useSelector(state => getDefaultDatasource(state, appId))
  const organization = useSelector(getCurrentOrganization)
  const [datasourceId, setDatasourceId] = useState(datasource?.id)
  const [ctaColorProps, setCtaColorProps] = useState({
    teal: true,
    secondary: true,
    gradient: false,
  })

  if (!datasourceId && datasource?.id) {
    setDatasourceId(datasource.id)
  }

  useEffect(() => {
    if (datasourceId) {
      dispatch(loadMetrics(appId, datasource.id))
    }
  }, [datasourceId])

  const daysLeft = getDaysLeftText(days)

  const trialTooltipTitle = 'Congrats! Your team is currently on a Free Trial!'

  const trialTooltipSubheader = 'What happens after the trial ends?'

  const trialTooltip = (
    <>
      <h1 className="trial-tooltip-header">Free Trial | {daysLeft}</h1>
      <p>{trialTooltipTitle}</p>
      <br />
      <h2 className="trial-tooltip-subheader">
        What's included in the Free Trial?
      </h2>
      <div className="tooltip-features-lists">
        <ul>
          <li>
            <Icon type="check-circle" color="white" />
            External Collections Integration
          </li>
          <li>
            <Icon type="check-circle" color="white" />
            Unlimited Custom Actions
          </li>
          <li>
            <Icon type="check-circle" color="white" />
            Collections API Access
          </li>
        </ul>
        <ul>
          <li>
            <Icon type="check-circle" color="white" />
            Access to all Custom Fonts
          </li>
          <li>
            <Icon type="check-circle" color="white" />
            Design Versions Access
          </li>
          <li>
            <Icon type="check-circle" color="white" />
            Full Geolocation Feature Access
          </li>
        </ul>
      </div>
      <br />
      <h2 className="trial-tooltip-subheader">{trialTooltipSubheader}</h2>
      <p>
        Don't worry! You'll still be able to keep building your app, just all
        the premium features will be disabled. You'll have access to your app as
        long as you like.
      </p>
      <Button
        text
        to="https://help.adalo.com/integrations"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn More
      </Button>
    </>
  )

  const full = getPercentageFull(metrics, datasource) === '100%'

  const handleMouseEnter = () => {
    setCtaColorProps(prev => ({
      ...prev,
      secondary: false,
      outlined: true,
    }))
  }

  const handleMouseLeave = () => {
    setCtaColorProps(prev => ({
      ...prev,
      secondary: true,
      outlined: false,
    }))
  }

  return (
    <div
      className={classNames('trial-warning-button-wrapper', {
        'trial-warning-button-wrapper--button': !bottomBar,
      })}
    >
      {trialState === 'during' && (
        <Tooltip
          tooltip={trialTooltip}
          placement="top"
          hideArrow
          containerClassname="trial-tooltip-container"
        >
          <div className="integrations-trial-tooltip-wrapper">
            <p className="integrations-trial-wrapper">
              <span role="img" aria-label="hourglass">
                ⏳
              </span>
              <span className="free-trial-label">Free Trial |</span>
              <b>{daysLeft}</b>
            </p>
          </div>
        </Tooltip>
      )}
      <Button
        small
        {...ctaColorProps}
        className={classNames(
          'trial-warning-button',
          full && 'trial-warning-button-full'
        )}
        onClick={async () => {
          // Track Mixpanel event for Upgrade Now click (client-side)
          startSessionRecording()

          // Track funnel event
          if (organization?.id) {
            await trackClickedUpgradeNow(
              organization.id,
              `Free Trial Banner - ${
                bottomBar ? 'bottom_bar' : 'top_bar'
              } - upgrade`
            )
          }

          handleClick()
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-adalo-id={!bottomBar ? 'top-bar-upgrade' : 'bottom-bar-upgrade'}
      >
        Upgrade Now
      </Button>
    </div>
  )
}

export default TrialBanner
